import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { pilot } from "@/lib/api";
import { fmtHours, fmtDate, bidDepLocal, bidArrLocal } from "@/lib/format";
import { Link } from "react-router-dom";
import { PlaneTakeoff, Clock, Award, MapPin, ArrowRight, Radio, Trash2, Loader2 } from "lucide-react";
import PilotHeader from "@/components/PilotHeader";
import RefreshBidsButton from "@/components/RefreshBidsButton";
import { BID_QUERY_OPTIONS } from "@/lib/bids";

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: pilot.dashboard, ...BID_QUERY_OPTIONS });

  // Rank name comes from the ranks table via the profile card, not from a
  // hardcoded ladder. The old rankTitle() map was a different airline's
  // structure — 7 of 8 names wrong, affecting 205 of 298 pilots. Same queryKey
  // as PilotHeader below, so this is served from cache.
  const { data: card } = useQuery({
    queryKey: ["profile-card", data?.user?.pid],
    queryFn: () => api.get("/pilot/profile-card").then((r) => r.data),
    enabled: !!data?.user?.pid,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  // Same as Delete on myvac's Flight Ops page: confirm with its wording, then
  // the bid is gone. The server refuses the one being flown.
  const qc = useQueryClient();
  const cancelBid = useMutation({
    mutationFn: (id) => pilot.cancelBid(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["my-bids"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || e.message || "Could not delete the bid"),
  });
  const confirmDelete = (b) => {
    if (window.confirm(`Are you sure you wish to be released from flight ${b.flt}?`)) {
      cancelBid.mutate(b.id);
    }
  };

  if (isLoading || !data) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }

  const u = data.user;
  const bids = data.bids || [];
  const active = data.active_flight;

  return (
    <div className="p-8 max-w-[1400px]" data-testid="dashboard-page">
      {/* Pilot header (name + rank hat / wings / epaulette + system date) */}
      <PilotHeader user={u} />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="dashboard-stats">
        <Stat icon={Clock}        label="Total time"   value={fmtHours(u.hours)} />
        {/* rank_title, not rank_short. `short_name` is a crew-schedule
            abbreviation — "Wb CA", "Rgnl FO" — and this tile sat directly under
            PilotHeader, which renders the full "Wide-body Captain" from the
            same card. One page, one rank, two spellings. Settings shows the
            title too, so the title is what this matches. */}
        <Stat icon={Award}        label="Rank"         value={card?.rank_title || card?.rank_short || "—"} />
        <Stat icon={MapPin}       label="Hub"          value={u.hub || "—"} />
        <Stat icon={PlaneTakeoff} label="Assignments"  value={bids.length} />
      </div>

      {/* Active flight banner */}
      {active && (
        <Link
          to="/tracking"
          data-testid="dashboard-active-banner"
          className="block mb-8 card-flat px-6 py-5 border-primary bg-primary/10 hover:bg-primary/20 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-primary flex items-center justify-center">
                <Radio className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <div className="overline mb-1 text-primary">Live flight in progress</div>
                <div className="text-lg font-mono">
                  {active.flt} · {active.orig} → {active.dest}
                </div>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-primary" />
          </div>
        </Link>
      )}

      {/* Bidded flights */}
      <section className="card-flat" data-testid="dashboard-bids">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="overline">Your assignments</div>
            <h2 className="text-lg font-bold">Bidded flights</h2>
          </div>
          <div className="flex items-center gap-4">
            <RefreshBidsButton />
            <Link
              to="#"
              onClick={(e) => { e.preventDefault(); window.open("https://myvac.flyvac.org/bids.php", "_blank"); }}
              className="text-xs text-primary hover:underline font-mono uppercase tracking-wider"
            >
              Bid on FlyVAC website →
            </Link>
          </div>
        </div>
        {bids.length === 0 ? (
          <div className="p-10 text-sm text-muted-foreground text-center">
            No bids yet. Bid on flights from the FlyVAC website and they will
            appear here when you come back to VACARS5, or press Refresh.
          </div>
        ) : (
          <div className="divide-y divide-border">
            <div className="grid grid-cols-12 px-6 py-2 overline bg-secondary">
              <div className="col-span-1">Flight</div>
              <div className="col-span-2">Route</div>
              <div className="col-span-1">Type</div>
              <div className="col-span-2">Date</div>
              <div className="col-span-1" title="Local time at the departure airport">Dep (L)</div>
              <div className="col-span-1" title="Local time at the arrival airport">Arr (L)</div>
              <div className="col-span-2">Fin · Reg</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {bids.map((b) => (
              <div key={b.id} className="grid grid-cols-12 px-6 py-3 items-center data-row" data-testid={`bid-row-${b.id}`}>
                <div className="col-span-1 font-mono text-sm">{b.flt || "—"}</div>
                <div className="col-span-2 font-mono text-sm">
                  {b.orig} → {b.dest}
                </div>
                <div className="col-span-1 font-mono text-sm">{b.type}</div>
                <div className="col-span-2 font-mono text-sm">{fmtDate(b.date)}</div>
                <div className="col-span-1 font-mono text-sm">{bidDepLocal(b.date)}</div>
                <div className="col-span-1 font-mono text-sm">{bidArrLocal(b.arrtime)}</div>
                <div className="col-span-2 font-mono text-sm">
                  {[b.tail, b.reg].filter(Boolean).join(" · ") || "—"}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <Link
                    to={`/init?bid=${b.id}&flt=${b.flt}&orig=${b.orig}&dest=${b.dest}&type=${b.type}`}
                    data-testid={`bid-init-${b.id}`}
                    className="text-xs bg-primary text-primary-foreground px-3 py-1 hover:brightness-110 transition-all font-mono uppercase tracking-wider"
                  >
                    Init
                  </Link>
                  {String(active?.assignment_id) === String(b.id) ? (
                    <span
                      title="This flight is in progress. Complete or cancel it before deleting the bid."
                      data-testid={`bid-delete-locked-${b.id}`}
                      className="p-1 text-muted-foreground opacity-30 cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => confirmDelete(b)}
                      disabled={cancelBid.isPending}
                      title="Delete this bid"
                      aria-label={`Delete bid for flight ${b.flt}`}
                      data-testid={`bid-delete-${b.id}`}
                      className="p-1 text-muted-foreground hover:text-primary disabled:opacity-50 transition-colors"
                    >
                      {cancelBid.isPending && cancelBid.variables === b.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Trash2 className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div className="px-6 py-2 text-[11px] text-muted-foreground" data-testid="bids-time-note">
              (L) = local time at that airport, as on FlyVAC. Not Zulu.
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// One face and one size for all four tiles. The rank used to be a smaller sans
// line between mono tiles and pilots read it as a different font. A rank name
// is up to 24 characters ("Wide-body First Officer") and wraps to two lines in
// a quarter-width tile, which is fine; showing the abbreviation instead is not.
function Stat({ icon: Icon, label, value }) {
  return (
    <div className="metric-card px-6 py-5">
      <div className="flex items-center gap-2 overline mb-2">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="telemetry-value text-2xl leading-tight tracking-tight">
        {value}
      </div>
    </div>
  );
}
